import React, { useCallback, useMemo } from 'react';
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { print } from 'graphql/language/printer';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import {
	changeOfCustomerContactDetailsSchema,
	changeOfCustomerContactDetailsUISchema,
} from '../../schemas/changeOfCustomerContactDetailsSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert/ExpiringAlert';
import Loading from '../../../Loading/Loading';
import { changeOfSupplierBPQuery } from '../../../../queries/Queries';

const getArrayEntityName = (arrayId) => {
	if (arrayId.includes('_telephones')) return 'Telephone';
	if (arrayId.includes('_emails')) return 'Email';
	if (arrayId.includes('_contacts')) return 'Contact';
	if (arrayId.includes('_customers')) return 'Customer';
	return 'Item';
};

const ArrayFieldTemplate = (props) => {
	const {
		title,
		required,
		description,
		items,
		canAdd,
		onAddClick,
		idSchema,
		readonly,
		disabled,
	} = props;

	const entityName = getArrayEntityName(idSchema && idSchema.$id ? idSchema.$id : '');
	const isTwoColumnLayout = entityName === 'Email' || entityName === 'Telephone' || entityName === 'Contact';

	return (
		<div className='mb-3'>
			{title && (
				<legend>
					{title}
					{required ? '*' : ''}
				</legend>
			)}
			{description}

			{items && (
				<div className='row'>
					{items.map((item) => (
						<div
							key={item.key}
							className={isTwoColumnLayout ? 'col-md-6 col-sm-12 mb-3' : 'col-12 mb-3'}
						>
							{item.children}
							{item.hasRemove && (
								<Button
									type='button'
									color='danger'
									onClick={item.onDropIndexClick(item.index)}
									disabled={disabled || readonly}
								>
									Remove {entityName}
								</Button>
							)}
						</div>
					))}
				</div>
			)}

			{canAdd && (
				<Button type='button' color='info' onClick={onAddClick} disabled={disabled || readonly}>
					Add {entityName}
				</Button>
			)}
		</div>
	);
};

const extractPostcode = (addressText) => {
	if (!addressText || typeof addressText !== 'string') return null;
	const match = addressText.match(/([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/i);
	return match ? match[1].toUpperCase().replace(/\s+/g, ' ').trim() : null;
};

const mapContactAddress = (locationAddress) => {
	if (!locationAddress || !locationAddress.addressLine1) {
		return {
			mailing_address_1: '',
			mailing_address_2: '',
			mailing_address_3: '',
			mailing_address_4: '',
			mailing_address_5: '',
			mailing_address_6: '',
			mailing_address_7: '',
			mailing_address_8: '',
			mailing_address_9: '',
			mailing_address_postcode: '',
		};
	}

	const fullAddress = locationAddress.addressLine1;
	const postcode = extractPostcode(fullAddress);

	const addressParts = fullAddress
		.split(',')
		.map((part) => {
			if (!postcode) return part;
			return part.replace(new RegExp(postcode.replace(/\s+/g, '\\s*'), 'i'), '');
		})
		.map((part) => part.trim())
		.filter((part) => part.length > 0);

	return {
		mailing_address_1: addressParts[0] || '',
		mailing_address_2: addressParts[1] || '',
		mailing_address_3: addressParts[2] || '',
		mailing_address_4: addressParts[3] || '',
		mailing_address_5: addressParts[4] || '',
		mailing_address_6: addressParts[5] || '',
		mailing_address_7: addressParts[6] || '',
		mailing_address_8: addressParts[7] || '',
		mailing_address_9: addressParts[8] || '',
		mailing_address_postcode: postcode || '',
	};
};

const findPrimaryContact = (contactList) => {
	if (!contactList || contactList.length === 0) return null;
	const primary = contactList.find((c) => c.user && c.user.isMdmIsowner);
	if (primary) return primary.user;
	return contactList[0] && contactList[0].user ? contactList[0].user : null;
};

const executeGraphQLQuery = async (query, variables = {}) => {
	const normalizedQuery = typeof query === 'string' ? query : print(query);

	const response = await axios.post(
		'/graphql',
		{
			query: normalizedQuery,
			variables,
		},
		{
			headers: {
				Authorization: `Bearer ${localStorage.token}`,
				'Content-Type': 'application/json',
			},
		},
	);

	if (response.data && response.data.errors && response.data.errors.length > 0) {
		throw new Error(response.data.errors[0].message || 'GraphQL query failed');
	}

	return response.data.data || {};
};

const buildCustomerPrefill = (bpData) => {
	const primaryContact = bpData ? findPrimaryContact(bpData.cRMBpartnerContactList) : null;
	const bpAddress = bpData && bpData.mdmContactAddress && bpData.mdmContactAddress.locationAddress
		? bpData.mdmContactAddress.locationAddress
		: null;
	const addressFields = mapContactAddress(bpAddress);

	return {
		customer_name: bpData ? (bpData.name || bpData.mdmCompanyName || '') : '',
		additional_information: '',
		customer_password: '',
		customer_password_efd: '',
		special_access: '',
		max_power_req: undefined,
		delete_address_data: false,
		...addressFields,
		contacts: [
			{
				contact_name: primaryContact
					? `${primaryContact.firstName || ''} ${primaryContact.lastName || ''}`.trim()
					: '',
				preferred_contact_method: undefined,
				telephones: [{ telephone_number: primaryContact && primaryContact.phone ? primaryContact.phone : '', fax_number: '' }],
				emails: [{ email_address: primaryContact && primaryContact.email ? primaryContact.email : '' }],
			},
		],
	};
};

const transformFormDataToPayload = (formData) => {
	const parsedMpan = parseInt(formData.mpan_core, 10);

	return {
		mpan_core: Number.isNaN(parsedMpan) ? formData.mpan_core : parsedMpan,
		customers: (formData.customers || []).map((customer) => ({
			customer_name: customer.customer_name,
			additional_information: customer.additional_information || null,
			customer_password: customer.customer_password || null,
			customer_password_efd: customer.customer_password_efd || null,
			special_access: customer.special_access || null,
			max_power_req: customer.max_power_req === undefined || customer.max_power_req === null ? null : customer.max_power_req,
			delete_address_data: !!customer.delete_address_data,
			mailing_address_1: customer.mailing_address_1 || null,
			mailing_address_2: customer.mailing_address_2 || null,
			mailing_address_3: customer.mailing_address_3 || null,
			mailing_address_4: customer.mailing_address_4 || null,
			mailing_address_5: customer.mailing_address_5 || null,
			mailing_address_6: customer.mailing_address_6 || null,
			mailing_address_7: customer.mailing_address_7 || null,
			mailing_address_8: customer.mailing_address_8 || null,
			mailing_address_9: customer.mailing_address_9 || null,
			mailing_address_postcode: customer.mailing_address_postcode,
			contacts: (customer.contacts || []).map((contact) => ({
				contact_name: contact.contact_name,
				preferred_contact_method: contact.preferred_contact_method || null,
				telephones: (contact.telephones || []).map((telephone) => ({
					telephone_number: telephone.telephone_number,
					fax_number: telephone.fax_number || null,
				})),
				emails: (contact.emails || []).map((email) => ({
					email_address: email.email_address,
				})),
			})),
		})),
	};
};

const ChangeOfCustomerContactDetailsForm = ({ onClose, initialData }) => {
	const { formContext, setFormContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initialData || {});

	const handleChange = useCallback(
		({ formData }) => {
			updateFormContext(formData);
		},
		[updateFormContext],
	);

	const fetchBPData = useCallback(async (mpanName) => {
		const whereClause =
			`id in (select bp.id from BusinessPartner bp join bp.mDMBPEstateList u ` +
			`where u.id in (select e.id from MDM_BPEstate e join e.mDMEstate es ` +
			`join es.mDMMeterPointList v where v.name = '${mpanName}'))`;

		const data = await executeGraphQLQuery(changeOfSupplierBPQuery, {
			where: whereClause,
		});

		if (data.businessPartner && data.businessPartner.length > 0) {
			return data.businessPartner[0];
		}

		return null;
	}, []);

	const handleMeterPointSelect = useCallback(
		async (meterPoint) => {
			if (!meterPoint || !meterPoint.name) {
				setFormContext((prev) => ({
					...prev,
					mpan_core: '',
					customers: [],
				}));
				return;
			}

			try {
				const bpData = await fetchBPData(meterPoint.name);
				setFormContext((prev) => ({
					...prev,
					mpan_core: meterPoint.name,
					customers: [buildCustomerPrefill(bpData)],
				}));
			} catch (error) {
				console.error('Failed to prefill customer data:', error);
				setFormContext((prev) => ({
					...prev,
					mpan_core: meterPoint.name,
				}));
			}
		},
		[fetchBPData, setFormContext],
	);

	const customValidate = useCallback((formData, errors) => {
		(formData.customers || []).forEach((customer, customerIndex) => {
			const hasAddressLine = [
				customer.mailing_address_1,
				customer.mailing_address_2,
				customer.mailing_address_3,
				customer.mailing_address_4,
				customer.mailing_address_5,
				customer.mailing_address_6,
				customer.mailing_address_7,
				customer.mailing_address_8,
				customer.mailing_address_9,
			].some((line) => typeof line === 'string' && line.trim().length > 0);

			if (!hasAddressLine && errors.customers && errors.customers[customerIndex]) {
				errors.customers[customerIndex].addError('At least one Mailing Address line (1-9) must be populated.');
			}
		});

		return errors;
	}, []);

	const transformErrors = useCallback((errors) => {
		return errors.map((error) => {
			if (error.property === '.mpan_core') {
				return { ...error, message: 'MPAN Core is a required property' };
			}

			if (error.name === 'minItems' && error.property.includes('.telephones')) {
				return { ...error, message: 'At least one telephone is required' };
			}

			if (error.name === 'minItems' && error.property.includes('.emails')) {
				return { ...error, message: 'At least one email is required' };
			}

			return error;
		});
	}, []);

	const handleSubmit = useCallback(
		async ({ formData }) => {
			setIsLoading(true);
			const payload = transformFormDataToPayload(formData);

			try {
				const response = await axios.post('/publishMessage', payload, {
					headers: {
						Authorization: `Bearer ${localStorage.token}`,
					},
					params: { queue: 'BOP.Dataflows.StartContactDetailsChange' },
				});

				setIsLoading(false);

				if (response.data === 'Message sent') {
					ReactDOM.render(
						<ExpiringAlert color='success' message='Contact Details Change request submitted successfully!' />,
						document.getElementById('alert').appendChild(document.createElement('div')),
					);
					onClose();
				} else {
					ReactDOM.render(
						<ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
						document.getElementById('alert').appendChild(document.createElement('div')),
					);
				}
			} catch (error) {
				setIsLoading(false);
				console.error('Error submitting Contact Details Change request:', error);
				ReactDOM.render(
					<ExpiringAlert color='danger' message='Error submitting request. Please check the console for details.' />,
					document.getElementById('alert').appendChild(document.createElement('div')),
				);
			}
		},
		[onClose, setIsLoading],
	);

	const uiSchemaWithWidgets = useMemo(
		() => ({
			...changeOfCustomerContactDetailsUISchema,
			mpan_core: {
				...changeOfCustomerContactDetailsUISchema.mpan_core,
				'ui:widget': (props) => (
					<MeterPointSelector
						{...props}
						placeholder='Select MPAN'
						includeAllStatuses={true}
						onMeterPointSelect={handleMeterPointSelect}
					/>
				),
			},
		}),
		[handleMeterPointSelect],
	);

	return (
		<div className='change-of-customer-contact-details-form'>
			<Form
				schema={changeOfCustomerContactDetailsSchema}
				uiSchema={uiSchemaWithWidgets}
				ArrayFieldTemplate={ArrayFieldTemplate}
				formContext={formContext}
				formData={formContext}
				transformErrors={transformErrors}
				validate={customValidate}
				showErrorList={false}
				onSubmit={handleSubmit}
				onChange={handleChange}
			>
				<div>
					<Button type='submit' disabled={isLoading} color='primary'>
						{isLoading && <Loading />}Submit Request
					</Button>
					&nbsp;&nbsp;&nbsp;
					<Button type='button' onClick={onClose}>
						Cancel
					</Button>
				</div>
			</Form>
		</div>
	);
};

export default ChangeOfCustomerContactDetailsForm;
